<template>
  <div class="py-5 container-fluid">
    <div id="topinfo">
      <div class="row mt-4 justify-content-center">
        <div class="col-12">
          <div class="mx-auto col-11 d-flex mb-2 flex-column card card-body blur shadow-blur">
            <div class="card-header mb-0 text-center">
              <h3 class="font-weight-bolder text-success text-gradient">Bagging</h3>
              <h5
                v-if="coating_booth && !calculationInProgress"
                class="mb-0"
              >
                for Line <i>{{ coating_booth.name }}</i>
              </h5>
            </div>
          </div>
          <div class="mx-auto col-11 d-flex flex-column card card-body blur shadow-blur mb-2">
            <div v-if="!calculationInProgress">
              <HopperSelector
                :hoppers="hoppers"
                :selected_hopper="selected_hopper"
                @update:selected_hopper="updateSelectedHopper($event)"
              />
            </div>
            <div
              v-if="calculationInProgress"
              class="card-body"
            >
              <div
                class="p-3 pb-0 card-header"
                style="text-align: center"
              >
                <h5>Updating Model with new baggings from lines:</h5>
                <div v-if="pending_coating_booths && pending_coating_booths.length > 0">
                  <div
                    v-for="booth in pending_coating_booths"
                    :key="booth.id"
                  >
                    <div
                      class="p-3"
                      style="background-color: #f8f9fa; border-radius: 5px"
                    >
                      {{ booth.name }}
                      <br />
                    </div>
                  </div>
                </div>
              </div>
              <div
                class="p-3 card-body"
                style="text-align: center"
              >
                <h6>The operation is going to take some minutes</h6>
                <br />
                <div style="width: 100%; height: 100%">
                  <div
                    class="spinner-border"
                    style="margin: auto; display: table; width: 100px; height: 100px"
                    role="status"
                  ></div>
                </div>
              </div>
            </div>
            <div
              v-if="selected_hopper !== null && !calculationInProgress"
              class="card-header row"
            >
              <div
                class="card-body"
                style="padding-top: 0.2rem"
              >
                <LineLayoutVisualization
                  v-if="showLineLayoutVisualization"
                  :coating-booth="coating_booth"
                />
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <h5 class="text-start mb-2">Guns Equalization</h5>
                </div>
                <div style="overflow-x: auto">
                  <div
                    v-if="currentPage == 0"
                    class="table-responsive"
                  >
                    <table
                      class="table table-sm text-center"
                      style="border-width: 0 !important"
                    >
                      <thead>
                        <tr>
                          <th>
                              Gun Name
                              <span class="text-xs text-secondary p-1">(ordered alphabetically)</span>
                          </th>
                          <th>Venturi Diameter</th>
                          <th>Empty bag weight</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="gun in powder_output_measurements.sort((a, b) => a.gun_name.localeCompare(b.gun_name, undefined, { numeric: true, sensitivity: 'base' }))"
                          :key="gun.gun_id"
                          :class="getValidationClass(gun.gun_id)"
                        >
                          <td>
                            <div>
                              <p>{{ gun.gun_name }}</p>
                            </div>
                          </td>
                          <td>
                            <div
                              v-if="
                                gun_data.find(g => g.gun_id == gun.gun_id).validation_status === 'valid' ||
                                gun_data.find(g => g.gun_id == gun.gun_id).validation_status === 'rejected' ||
                                (!rebaggingEditableGuns.includes(gun.gun_id.toFixed(0)) &&
                                  populateWithLatestBaggingMeasures)
                              "
                            >
                              <p>{{ gun.diameter }}</p>
                            </div>
                            <div v-else>
                              <input
                                v-model.lazy="gun.diameter"
                                type="number"
                                class="form-control form-control-sm mx-auto text-center"
                                min="0"
                                :placeholder="`Diameter [${$store.state.units_system.metric.distance_minor}] `"
                                style="width: 60%; max-width: 190px"
                                @blur="
                                  diameterWithinRangeCheck($event.target.value, gun);
                                  saveFormProgress();
                                "
                                @keydown="numericPositiveOnly"
                                @change="
                                  updated_powder_output_measurements = true;
                                  is_logistic_fitting_completed = false;
                                "
                              />
                            </div>
                          </td>
                          <td>
                            <div
                              v-if="
                                gun_data.find(g => g.gun_id == gun.gun_id).validation_status === 'valid' ||
                                gun_data.find(g => g.gun_id == gun.gun_id).validation_status === 'rejected' ||
                                (!rebaggingEditableGuns.includes(gun.gun_id.toFixed(0)) &&
                                  populateWithLatestBaggingMeasures)
                              "
                            >
                              <p>{{ gun.empty_bag_weight }}</p>
                            </div>
                            <div v-else>
                              <input
                                v-model.lazy="gun.empty_bag_weight"
                                type="number"
                                class="form-control form-control-sm mx-auto text-center"
                                min="0"
                                :placeholder="`Bag weight [${$store.state.units_system[$store.state.user_data.unit_system].grams}] `"
                                style="width: 60%; max-width: 190px"
                                @keydown="numericPositiveOnly"
                                @change="
                                  updated_powder_output_measurements = true;
                                  is_logistic_fitting_completed = false;
                                  autoFillEmptyBagWeight($event.target.value);
                                  saveFormProgress();
                                "
                              />
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div
                    v-if="currentPage != 0"
                    class="table-responsive"
                  >
                    <table
                      class="table table-sm text-center"
                      style="border-width: 0 !important"
                    >
                      <thead>
                        <tr>
                          <th>
                              Gun Name
                              <span class="text-xs text-secondary p-1">(ordered alphabetically)</span>
                          </th>
                          <th>Setting {{ currentPage }}</th>
                          <th>Weight {{ currentPage }}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(gun, measurement_index) in powder_output_measurements.sort((a, b) => a.gun_name.localeCompare(b.gun_name, undefined, { numeric: true, sensitivity: 'base' }))"
                          :key="gun.gun_id"
                          :class="getValidationClass(gun.gun_id)"
                        >
                          <td>
                            <p>{{ gun.gun_name }}</p>
                          </td>
                          <td>
                            <div
                              v-if="
                                gun_data.find(g => g.gun_id == gun.gun_id).validation_status === 'valid' ||
                                gun_data.find(g => g.gun_id == gun.gun_id).validation_status === 'rejected' ||
                                (!rebaggingEditableGuns.includes(gun.gun_id.toFixed(0)) &&
                                  populateWithLatestBaggingMeasures)
                              "
                            >
                              <p>{{ gun.gun_measurements[currentPage - 1].setting }}</p>
                            </div>
                            <div v-else>
                              <input
                                v-model="gun.gun_measurements[currentPage - 1].setting"
                                type="number"
                                class="form-control form-control-sm mx-auto text-center"
                                placeholder="Setting"
                                min="0"
                                style="width: 60%; max-width: 190px"
                                @keydown="numericPositiveOnly"
                                @change="
                                  autoFillSettings(gun.gun_id, currentPage - 1, measurement_index === 0);
                                  saveFormProgress();
                                "
                              />
                            </div>
                          </td>
                          <td>
                            <div
                              v-if="
                                gun_data.find(g => g.gun_id == gun.gun_id).validation_status === 'valid' ||
                                gun_data.find(g => g.gun_id == gun.gun_id).validation_status === 'rejected' ||
                                (!rebaggingEditableGuns.includes(gun.gun_id.toFixed(0)) &&
                                  populateWithLatestBaggingMeasures)
                              "
                            >
                              <p>{{ gun.gun_measurements[currentPage - 1].weight }}</p>
                            </div>
                            <div v-else>
                              <input
                                v-model="gun.gun_measurements[currentPage - 1].weight"
                                type="number"
                                class="form-control form-control-sm mx-auto text-center"
                                placeholder="Weight (g)"
                                min="0"
                                style="width: 60%; max-width: 190px"
                                @keydown="numericPositiveOnly"
                                @blur="PowderAmountWeightCheck($event.target.value, gun.gun_id)"
                                @change="
                                  updated_powder_output_measurements = true;
                                  is_logistic_fitting_completed = false;
                                  saveFormProgress();
                                "
                              />
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div>
                    <soft-pagination
                      class="justify-content-center mt-3 mb-3"
                      color="success"
                      aria-label="Experiment thickness measures input pages"
                    >
                      <soft-pagination-item
                        prev
                        :disabled="isInFirstPage"
                        @click="onClickPrevPage"
                      />
                      <soft-pagination-item
                        label="0"
                        :active="currentPage === 0"
                        @click="currentPage = 0"
                      />
                      <soft-pagination-item
                        label="1"
                        :active="currentPage === 1"
                        @click="currentPage = 1"
                      />
                      <soft-pagination-item
                        label="2"
                        :active="currentPage === 2"
                        @click="currentPage = 2"
                      />
                      <soft-pagination-item
                        label="3"
                        :active="currentPage === 3"
                        @click="currentPage = 3"
                      />
                      <soft-pagination-item
                        next
                        :disabled="isInLastPage"
                        @click="onClickNextPage"
                      />
                    </soft-pagination>
                  </div>
                  <div
                    v-if="!isInLastPage"
                    class="row-12 float-end mt-4 mb-2 mx-2"
                  >
                    <soft-button
                      color="success"
                      size="sm"
                      variant="contained"
                      @click="onClickNextPage"
                    >
                      Continue to Measurement {{ currentPage + 1 }}
                    </soft-button>
                  </div>
                </div>
                <div
                  v-if="showFitPowderAmountMeasurementsButton"
                  class="col-12 text-center mt-4 mb-4"
                >
                  <button
                    type="button"
                    class="mt-4 mb-2 text-center btn"
                    :class="emptyPowderOutputMeasurements ? 'bg-gradient-secondary' : 'bg-gradient-success'"
                    style="width: 90%"
                    @click="calculateGunsEqualization()"
                  >
                    Fit Powder Amount Measurements
                  </button>
                </div>
              </div>
              <div
                v-if="gunsEqualizationCompleted"
                class="mb-4"
              >
                <div
                  v-if="is_training_ongoing"
                  class="color-legend mb-3"
                >
                  <div class="legend-item">
                    <span class="color-box valid-row"></span>
                    <span>Valid</span>
                  </div>
                  <div class="legend-item">
                    <span class="color-box accepted-row"></span>
                    <span>Review Bagging</span>
                  </div>
                  <div class="legend-item">
                    <span class="color-box rejected-row"></span>
                    <span>Service</span>
                  </div>
                </div>
                <div class="row">
                  <div class="col-12">
                    <div class="card card-body blur shadow-blur">
                      <div class="card-header">
                        <h4 class="text-center">Guns Equalization Graph</h4>
                      </div>
                      <div class="card-body">
                        <steel-case-guns-equalization-chart :datasets="guns_equalization_datasets" />
                      </div>
                    </div>
                  </div>
                </div>
                <div
                  v-if="allGunsEvaluated"
                  class="row justify-content-center mt-4 mb-4"
                >
                  <div class="col-5 text-center mt-4 mb-4">
                    <soft-button
                      type="button"
                      class="mt-4 mb-2 text-center btn bg-gradient-success"
                      style="width: 90%"
                      @click="triggerTraining"
                    >
                      <!-- TODO: Put back original text for Michigan users after predictive model is added -->
                      {{ isMichiganRegionUser ? "Finish Bagging & Update Recipes" : "Finish bagging & start training process" }}
                    </soft-button>
                  </div>
                  <div
                    v-if="!populateWithLatestBaggingMeasures"
                    class="col-5 text-center mt-4 mb-4"
                  >
                    <soft-button
                      type="button"
                      class="mt-4 mb-2 text-center btn bg-gradient-success"
                      style="width: 90%"
                      @click="open_new_bagging_modal = true"
                    >
                      Add Another Bagging
                    </soft-button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="mx-auto col-11 d-flex flex-column card card-body blur shadow-blur mb-2">
            <div class="accordion">
              <div class="accordion-item mb-0">
                <h5>
                  <button
                    class="accordion-button collapsed mb-0 p-2"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target="#panelsStayOpen-collapseParams"
                    aria-expanded="false"
                    aria-controls="panelsStayOpen-collapseParams"
                    style="padding: 0"
                  >
                    <p
                      class="font-weight-bolder mt-1"
                      style="font-weight: 700 !important; font-size: 1.05rem; vertical-align: middle; margin-bottom: 0"
                    >
                      Bagging History
                      <i
                        class="fa fa-chevron-down"
                        aria-hidden="true"
                      ></i>
                      <i
                        class="fa fa-chevron-up"
                        aria-hidden="true"
                      ></i>
                    </p>
                  </button>
                </h5>
                <div
                  id="panelsStayOpen-collapseParams"
                  class="accordion-collapse collapse"
                  aria-labelledby="panelsStayOpen-headingOne"
                >
                  <div class="accordion-body mt-0 pt-0">
                    <div
                      v-if="previous_baggings.length > 0"
                      style="overflow-x: auto"
                    >
                      <div class="card-body">
                        <table class="table table-borderless table-sm mb-0">
                          <tbody style="display: flex; flex-direction: column; gap: 9px">
                            <tr
                              v-for="bagging in previous_baggings"
                              :key="bagging.id"
                              class="align-middle previous-bagging-row"
                              data-bs-toggle="modal"
                              data-bs-target="#modal-form"
                              @click="openBaggingDetailModal(bagging)"
                            >
                              <td
                                class="py-2 px-3"
                                style="font-weight: 600; width: 90px"
                              >
                                Created:
                              </td>
                              <td
                                class="py-2 px-2"
                                style="width: 140px"
                              >
                                <span class="hover-underline">{{ bagging.created_at }}</span>
                              </td>
                              <td
                                class="py-2 px-3 text-end"
                                style="width: 100%"
                                colspan="2"
                              >
                                <span style="font-weight: 600; color: #555">Hopper:</span>
                                <span class="hover-underline ms-2">{{ bagging.hopper.name }}</span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <NewBaggingModal v-if="open_new_bagging_modal" />
          <BaggingGunReviewModal
            v-if="currentGunForReview"
            :gun="currentGunForReview"
            :gun-dataset="getGunRegressionDataset(currentGunForReview)"
            :gun-powder-output-measurements="getGunPowderOutputMeasurements(currentGunForReview)"
            @validated="handleGunValidation"
          />
        </div>
        <NewBaggingModal
          v-if="open_new_bagging_modal"
          @validated="onValidated"
        />
        <div
          id="modal-form"
          ref="modal"
          class="modal fade"
          tabindex="-1"
          role="dialog"
          aria-labelledby="modal-form"
          aria-hidden="true"
          style="z-index: 1050 !important"
        >
          <BaggingDetailModal
            :key="open_bagging_detail_modal ? selected_bagging.id : ''"
            :bagging-diagram-data-per-gun="bagging_diagram_data_per_gun"
            :hopper-name="open_bagging_detail_modal ? selected_bagging.hopper.name : ''"
            :bagging-creation-timestamp="open_bagging_detail_modal ? selected_bagging.created_at : ''"
            :show-modal-loading-spinner="show_modal_loading_spinner"
            @close="open_bagging_detail_modal = false"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import setTooltip from "@/assets/js/tooltip.js";

import { numericPositiveOnly } from "@/views/composables.js";

import BaggingGunReviewModal from "./components/BaggingGunReviewModal.vue";
import NewBaggingModal from "./components/NewBaggingModal.vue";
import BaggingDetailModal from "./components/BaggingDetailModal.vue";
import SoftButton from "@/components/SoftButton.vue";
import SoftPagination from "@/components/SoftPagination.vue";
import SoftPaginationItem from "@/components/SoftPaginationItem.vue";
import SteelCaseGunsEqualizationChart from "./components/SteelCaseGunsEqualizationChart.vue";
import LineLayoutVisualization from "./components/LineLayoutVisualization.vue";
import HopperSelector from "./components/HopperSelector.vue";

export default {
  name: "Bagging",
  components: {
    BaggingDetailModal,
    BaggingGunReviewModal,
    NewBaggingModal,
    SoftButton,
    SoftPagination,
    SoftPaginationItem,
    SteelCaseGunsEqualizationChart,
    LineLayoutVisualization,
    HopperSelector,
  },
  props: {
    lineId: {
      type: String,
      default: "",
    },
    powderOutputMeasurementId: {
      type: String,
      default: "",
    },
    populateWithLatestBaggingMeasures: {
      type: Boolean,
      default: false,
    },
    rebaggingEditableGuns: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    this.$i18n.locale = this.$store.state.user_data.language;

    return {
      powder_output_measurements: [],
      hoppers: [],
      selected_hopper: null,
      coating_booth: null,
      updated_powder_output_measurements: false,
      is_logistic_fitting_completed: false,
      gun_data: [],
      guns_equalization_datasets: [],
      currentPage: 0,
      firstPage: 0,
      lastPage: 3,
      is_training_ongoing: false,
      lock_dropdown: false,
      open_new_bagging_modal: false,
      showLineLayoutVisualization: false,
      // Previous baggings section variables
      previous_baggings: [],
      selected_bagging: null,
      open_bagging_detail_modal: false,
      show_modal_loading_spinner: false,
      bagging_diagram_data_per_gun: [],
    };
  },
  computed: {
    showFitPowderAmountMeasurementsButton() {
      return this.currentPage === this.lastPage && !this.allGunsEvaluated;
    },
    gunsEqualizationCompleted() {
      let guns_equalization_completed =
        (this.selected_hopper !== null &&
          !this.emptyPowderOutputMeasurements &&
          this.is_logistic_fitting_completed == true) ||
        this.allGunsEvaluated;
      return guns_equalization_completed;
    },
    maxPairs() {
      let maxPairs = 0;
      this.powder_output_measurements.forEach(gun => {
        if (gun.gun_measurements.length > maxPairs) {
          maxPairs = gun.gun_measurements.length;
        }
      });
      return maxPairs;
    },
    emptyPowderOutputMeasurements() {
      const empty_powder_output_measurements = this.powder_output_measurements.some(gun =>
        gun.gun_measurements.some(pair => pair.setting == "" || pair.weight == ""),
      );
      const empty_bag_weight_measurements = this.powder_output_measurements.some(
        gun => gun.empty_bag_weight == null || gun.empty_bag_weight == "",
      );
      const empty_diameter_measurements = this.powder_output_measurements.some(
        gun => gun.diameter == null || gun.diameter == "",
      );

      return empty_powder_output_measurements || empty_bag_weight_measurements || empty_diameter_measurements;
    },
    failingGunsToEvaluate() {
      if (!this.gun_data) {
        return [];
      }

      if (this.populateWithLatestBaggingMeasures) {
        return this.gun_data.filter(
          gun =>
            gun.validation_status === "review_bagging" && this.rebaggingEditableGuns.includes(gun.gun_id.toFixed(0)),
        );
      }
      return this.gun_data.filter(gun => gun.validation_status === "review_bagging");
    },
    currentGunForReview() {
      if (this.failingGunsToEvaluate.length > 0 && this.guns_equalization_datasets.length > 0) {
        return this.failingGunsToEvaluate[0];
      }
      return null;
    },
    allGunsEvaluated() {
      if (!this.gun_data) {
        return false;
      }
      return this.gun_data.every(
        gun =>
          gun.validation_status === "valid" ||
          gun.validation_status === "rejected" ||
          (!this.rebaggingEditableGuns.includes(gun.gun_id.toFixed(0)) && this.populateWithLatestBaggingMeasures),
      );
    },
    calculationInProgress() {
      return this.$store.state.multi_output_gp_model_training_in_progress_ids.includes(this.$store.state.user_data.id);
    },
    isInFirstPage() {
      return this.currentPage === this.firstPage;
    },
    isInLastPage() {
      return this.currentPage === this.lastPage;
    },
    isMichiganRegionUser() {
      return this.$store.state.user_data.steelcase_service_region === "MICHIGAN";
    },
  },
  watch: {
    "$store.state.multi_output_gp_model_training_in_progress_ids": async function () {
      if (!this.$store.state.multi_output_gp_model_training_in_progress_ids.includes(this.$store.state.user_data.id)) {
        this.is_training_ongoing = false;
      }
    },
    async allGunsEvaluated(value) {
      if (value) {
        this.$swal({
          title: "All Guns Evaluated",
          text: "Ready to proceed to training",
          icon: "success",
          timer: 2000,
          timerProgressBar: true,
          confirmButtonText: "OK",
        });
      }
    },
  },
  mounted() {
    this.$store.state.isAbsolute = true;
    setTooltip();
    this.getData();
  },
  beforeUnmount() {
    this.$store.state.isAbsolute = false;
  },
  methods: {
    numericPositiveOnly,
    async getData() {
      await this.getCoatingBooth();
      await this.getPendingCoatingBooths();

      this.gun_data = this.getGuns();

      await this.getHoppers();

      if (this.populateWithLatestBaggingMeasures) {
        console.log("Fetching latest bagging measures...");

        await this.getLatestBaggingMeasures();
        this.showReBaggingPopUpMessage();
      } else {
        this.powder_output_measurements = this.generateMeasurementsForEachGun();
        this.getTempPowderOutputMeasurementData();
      }

      if (this.selected_hopper !== null) {
        this.showLineLayoutVisualization = true;
      }

      await this.getPreviousBaggingsHistory();
    },
    async onValidated(validated_response) {
      if (validated_response.status === "cancel") {
        this.open_new_bagging_modal = false;
      } else if (validated_response.status === "new_bagging") {
        await this.storeCurrentBaggingData();
        this.open_new_bagging_modal = false;
        try {
          let response = await axios.post("/api/v1/steelcase/temppowderoutputmeasurement/", {});
          const powderOutputMeasurementId = response.data.id;
          const dataForm = {
            coating_booth_id: validated_response.line_id,
            hopper_id: validated_response.hopper_id !== null ? validated_response.hopper_id : null,
          };

          await axios.patch(
            `/api/v1/steelcase/temppowderoutputmeasurement/` + powderOutputMeasurementId + `/`,
            dataForm,
          );
          this.$store.state.isLoading = true;
          this.$router.push({
            name: "Bagging",
            params: { lineId: validated_response.line_id, powderOutputMeasurementId: powderOutputMeasurementId },
          });
          await this.$swal({
            title: "New Bagging Created",
            text: "New bagging created successfully.",
            icon: "success",
            confirmButtonText: "OK",
          });
          window.location.reload();
        } catch (error) {
          console.error("Error saving form progress:", error);
        }
      }
    },
    async storeCurrentBaggingData() {
      try {
        const filtered_powder_output_measurements = this.powder_output_measurements
          .map(gun => ({
            gun_id: gun.gun_id,
            empty_bag_weight: gun.empty_bag_weight,
            gun_measurements: gun.gun_measurements,
            diameter: gun.diameter,
          }));
        let body = {
          coating_booth_id: this.coating_booth.id,
          hopper_id: this.selected_hopper.id,
          fitted_gun_models: this.gun_data.map(gun => ({
            gunID: gun.gun_id,
            logistic_model: { L: gun.L, k: gun.k, x0: gun.x0 },
            valid_fit: gun.validation_status === "valid",
            diameter: this.powder_output_measurements.find(pom => pom.gun_id === gun.gun_id).diameter,
          })),
          powder_output_measurements: filtered_powder_output_measurements,
        };
        await axios.post("api/v1/steelcase/storebaggingdata/", body);
      } catch (error) {
        console.error(error);
      }
    },
    autoFillEmptyBagWeight(empty_bag_weight_value) {
      this.powder_output_measurements.forEach(gun => {
        gun.empty_bag_weight = empty_bag_weight_value;
      });
    },
    autoFillSettings(gun_id, setting_index, isFirstRow) {
      if (!isFirstRow) { // Only works for the first row to propagate downwards
        return;
      }

      const gun = this.powder_output_measurements.find(gun => gun.gun_id === gun_id);
      const inputValue = gun.gun_measurements[setting_index].setting;
      const emptyInputValue = inputValue === "" || inputValue === null;

      const otherGuns = this.powder_output_measurements.filter(gun => gun.gun_id !== gun_id);

      const isRestOfColumnEmpty = otherGuns
        .every(
          gun =>
            gun.gun_measurements[setting_index].setting === "" || gun.gun_measurements[setting_index].setting === null,
        );

      if (!emptyInputValue && isRestOfColumnEmpty) {
        this.powder_output_measurements.forEach(gun => {
          gun.gun_measurements[setting_index].setting = inputValue;
        });
        return;
      }

      if (emptyInputValue && !isRestOfColumnEmpty) {
        const firstSettingOtherGuns = otherGuns[0].gun_measurements[setting_index].setting;
        const allValuesAreTheSame = otherGuns.every(gun => gun.gun_measurements[setting_index].setting === firstSettingOtherGuns);

        if (allValuesAreTheSame) {
          this.powder_output_measurements.forEach(gun => {
            gun.gun_measurements[setting_index].setting = "";
          });
        }
      }
    },
    async getPendingCoatingBooths() {
      try {
        let response = await axios.get("api/v1/steelcase/coatingbooth/");
        this.pending_coating_booths = response.data.filter(element => element.pending_training);
      } catch (error) {
        console.error(error);
      }
    },
    async getCoatingBooth() {
      try {
        const response = await axios.get("api/v1/steelcase/coatingbooth/" + this.lineId + "/");
        this.coating_booth = response.data;
        this.reciprocator_side_A = this.coating_booth.reciprocator_side_A;
        this.reciprocator_side_B = this.coating_booth.reciprocator_side_B;

        if (this.coating_booth.reciprocator_side_bottom) {
          this.reciprocator_side_bottom = this.coating_booth.reciprocator_side_bottom;
        }
      } catch (error) {
        console.error(error);
      }
    },
    showReBaggingPopUpMessage() {
      this.$swal({
        title: `Re-bagging faulty Guns of ${this.coating_booth.name}`,
        text: "You have been sent to a re-bagging. This page is pre-filled with your latest bagging measures, and only the faulty guns that need re-bagging are available. Please address the issues and then proceed with the re-bagging of the faulty guns.",
        icon: "info",
        confirmButtonText: "OK",
      });
    },
    async getLatestBaggingMeasures() {
      const response = await axios.get("api/v1/steelcase/latestbaggingdata/" + this.lineId + "/");
      const hopper_id = response.data.hopper_id;
      this.powder_output_measurements = response.data.powder_output_measurements;

      const hopper = this.hoppers.find(hopper => hopper.id === hopper_id);

      if (hopper) {
        await this.updateSelectedHopper(hopper);
      } else {
        this.$swal({
          title: "Error",
          text: "Hopper of last Bagging was not found in list of available hoppers.",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    },
    generateMeasurementsForEachGun() {
      return this.gun_data.map(gun => ({
        gun_index: gun.gun_index,
        gun_id: gun.gun_id,
        gun_name: gun.gun_name,
        empty_bag_weight: null,
        diameter: null,
        gun_measurements: [
          {
            setting: 1.4,
            weight: "",
          },
          {
            setting: 2.2,
            weight: "",
          },
          {
            setting: 3.0,
            weight: "",
          },
        ],
      }));
    },
    enrichGunData(gunData) {
      const gun = { ...gunData };
      gun.gun_id = gunData.id;
      gun.gun_name = gunData.name;
      gun.L = null;
      gun.k = null;
      gun.x0 = null;
      gun.validation_status = null;
      return gun;
    },
    getGuns() {
      try {
        const guns_array = [
          ...this.reciprocator_side_A.guns,
          ...this.reciprocator_side_B.guns,
          ...(this.reciprocator_side_bottom ? this.reciprocator_side_bottom.guns : []),
        ];

        const enriched_guns = guns_array.map(gunData => this.enrichGunData(gunData));
        enriched_guns.sort((a, b) => a.gun_index - b.gun_index);

        return enriched_guns;
      } catch (error) {
        console.error(error);
      }
    },
    async handleHopperSelection() {
      this.showLineLayoutVisualization = true;
      await this.saveFormProgress();
    },
    async updateSelectedHopper(hopper) {
      this.selected_hopper = hopper;
      await this.handleHopperSelection();
      // for testing purposes
      // this.populatePowderOutputMeasurements();
    },
    async calculateGunsEqualization() {
      if (this.emptyPowderOutputMeasurements) {
        this.$swal({
          title: "Empty Measurements",
          text: "Please fill all the Powder Output Measurement fields",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      this.$store.state.isLoading = true;
      try {
        const filtered_powder_output_measurements = this.powder_output_measurements
          .map(gun => ({
            gun_id: gun.gun_id,
            diameter: gun.diameter,
            empty_bag_weight: gun.empty_bag_weight,
            gun_measurements: gun.gun_measurements,
          }));
        const response = await axios.post("api/v1/steelcase/fitgunmodelsfrompowderoutputmeasures/", {
          powder_output_measurements: filtered_powder_output_measurements,
        });

        if (!this.lock_dropdown) {
          this.lock_dropdown = true;
        }

        this.mapGunsLogisticModels(response.data);
        await this.getGunsEqualizationData();

        this.$store.state.isLoading = false;

        this.is_logistic_fitting_completed = true;
      } catch (error) {
        console.error(error);
        this.lock_dropdown = false;
        this.$store.state.isLoading = false;
        this.$swal({
          title: "Error",
          text: `An error occurred while updating the venturi diameters:\n${error.message}`,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    },
    async getGunsEqualizationData() {
      try {
        let guns_logistic_data = this.gun_data.reduce((acc, gun) => {
          acc[gun.gun_name] = {
            L: gun.L,
            k: gun.k,
            x0: gun.x0,
          };
          return acc;
        }, {});
        let body = {
          coating_booth_id: this.coating_booth.id,
          data: guns_logistic_data,
        };
        let response = await axios.post("api/v1/steelcase/computelogisticdiagrampergun/", body);
        this.guns_equalization_datasets = response.data;
      } catch (error) {
        console.error(error);
      }
    },
    getGunRegressionDataset(gun) {
      return this.guns_equalization_datasets.find(dataset => dataset.label === `${gun.gun_name}`);
    },
    handleGunValidation({ gun, status }) {
      gun.validation_status = status;
    },
    mapGunsLogisticModels(data) {
      const updated_guns_array = this.gun_data.map(gun => {
        if (gun.validation_status !== "valid" && gun.validation_status !== "rejected") {
          const coeffs = data.find(gun_coeffs => gun_coeffs.gunID === gun.gun_id);

          return coeffs
            ? {
                ...gun,
                L: coeffs.logistic_model.L,
                k: coeffs.logistic_model.k,
                x0: coeffs.logistic_model.x0,
                validation_status:
                  coeffs.valid_fit ||
                  (!this.rebaggingEditableGuns.includes(gun.gun_id) && this.populateWithLatestBaggingMeasures)
                    ? "valid"
                    : "review_bagging",
              }
            : gun;
        }
        return gun;
      });

      this.gun_data = updated_guns_array;
    },
    clearWeightMeasurements(powder_output_measurements) {
      const powderoutput_measurements_cleared_weights = powder_output_measurements.map(gun => ({
        empty_bag_weight: null,
        gun_measurements: gun.gun_measurements.map(pair => ({
          setting: pair.setting,
          weight: "",
        })),
      }));

      return powderoutput_measurements_cleared_weights;
    },
    populatePowderOutputMeasurements() {
      // For testing purposes only
      this.powder_output_measurements = this.powder_output_measurements.map(gun => {
        const updatedGun = {
          ...gun,
          diameter: gun.diameter || (Math.random() * (6 - 5) + 5).toFixed(2),
          empty_bag_weight: gun.empty_bag_weight == null || gun.empty_bag_weight === "" ? 17 : gun.empty_bag_weight,
          gun_measurements: gun.gun_measurements.map(pair => ({
            ...pair,
            weight: pair.setting
              ? parseInt(pair.setting) == 1
                ? parseFloat((Math.random() * 15 + gun.empty_bag_weight + 70).toFixed(0))
                : parseInt(pair.setting) == 2
                  ? parseFloat((Math.random() * 15 + gun.empty_bag_weight + 190).toFixed(0))
                  : parseInt(pair.setting) == 3
                    ? parseFloat((Math.random() * 15 + gun.empty_bag_weight + 400).toFixed(0))
                    : parseFloat(pair.weight)
              : parseFloat(pair.weight),
          })),
        };
        return updatedGun;
      });
    },
    async getTempPowderOutputMeasurementData() {
      if (!this.$route.params.powderOutputMeasurementId) {
        return;
      }

      try {
        const response = await axios.get(
          "api/v1/steelcase/temppowderoutputmeasurement/" + this.$route.params.powderOutputMeasurementId + "/",
        );

        if (response.data.hopper_id !== null) {
          this.updateSelectedHopper(this.hoppers.find(hopper => hopper.id == response.data.hopper_id));
        }
        if (response.data.powder_output_measurements) {
          this.powder_output_measurements = response.data.powder_output_measurements;
          this.powder_output_measurements.sort((a, b) => a.gun_index - b.gun_index);
        }
      } catch (error) {
        console.error(error);
      }
    },
    async saveFormProgress() {
      if (!this.powderOutputMeasurementId) {
        return;
      }

      try {
        const dataForm = {
          coating_booth_id: this.lineId,
          hopper_id: this.selected_hopper !== null ? this.selected_hopper.id : null,
          powder_output_measurements:
            this.powder_output_measurements.length !== 0 ? this.powder_output_measurements : null,
        };

        await axios.patch(
          `/api/v1/steelcase/temppowderoutputmeasurement/` + this.powderOutputMeasurementId + `/`,
          dataForm,
        );
      } catch (error) {
        console.error("Error saving form progress:", error);
      }
    },
    async successSwal() {
      try {
        await this.$swal({
          title: "Gun Equalization success",
          text: "Powder amounts fine tune adjustment calculation successful",
          icon: "success",
          confirmButtonText: "OK",
        });
      } catch (error) {
        console.error(error);
      }
    },
    async getHoppers() {
      if (this.coating_booth.id === null) {
        return;
      }
      try {
        let response = await axios.get("api/v1/steelcase/hopper/coating-booth/" + this.coating_booth.id + "/");
        this.hoppers = response.data;
      } catch (error) {
        console.error(error);
      }
    },
    async getPreviousBaggingsHistory() {
      try {
        const response = await axios.get("api/v1/steelcase/previous-baggings/" + this.coating_booth.id + "/");
        this.previous_baggings = response.data;
      } catch (error) {
        console.error(error);
      }
    },
    async openBaggingDetailModal(bagging) {
      this.show_modal_loading_spinner = true;
      this.bagging_diagram_data_per_gun = [];
      this.selected_bagging = bagging;
      const logistic_diagram_request_body = {};
      this.gun_data.forEach(gun => {
        const measurement_data = bagging.measurements[gun.gun_id];
        if (measurement_data && measurement_data.coefficients_fitted) {
          logistic_diagram_request_body[gun.gun_name] = {
            L: measurement_data.coefficients_fitted.L,
            k: measurement_data.coefficients_fitted.k,
            x0: measurement_data.coefficients_fitted.x0,
          };
        }
      });

      const response_datapoints = await axios.post("api/v1/steelcase/computelogisticdiagrampergun/", {
        coating_booth_id: this.coating_booth.id,
        data: logistic_diagram_request_body,
      });

      this.gun_data.forEach(gun => {
        const logistic_datapoints = response_datapoints.data.find(d => d.label === gun.gun_name);
        const datapoints_powder_output_measurements = bagging.measurements[gun.gun_id].powder_amount.map(
          (powder_amount, index) => ({
            x: powder_amount,
            y: bagging.measurements[gun.gun_id].powder_weight[index],
          }),
        );

        this.bagging_diagram_data_per_gun.push({
          gun_id: gun.gun_id,
          gun_name: gun.gun_name,
          venturi_diameter: bagging.measurements[gun.gun_id].diameter,
          empty_bag_weight: bagging.measurements[gun.gun_id].empty_bag_weight,
          datapoints_logistic_fit: logistic_datapoints,
          datapoints_powder_output_measurements: datapoints_powder_output_measurements,
        });
      });
      this.open_bagging_detail_modal = true;
      this.show_modal_loading_spinner = false;
    },
    diameterWithinRangeCheck(target_value, gun) {
      target_value = this.formatDiameter(target_value);
      if (target_value != "" && (target_value < 5 || target_value > 10)) {
        this.$swal({
          title: "Invalid Diameter: " + target_value.toString() + "mm",
          text: "The diameter must be between 5 and 10 mm.",
          icon: "error",
          confirmButtonText: "OK",
        }).then(() => {
          gun.diameter = "";
          this.saveFormProgress();
        });
      } else {
        gun.diameter = target_value;
      }
    },
    formatDiameter(input) {
      const digitsOnly = input.toString().replace(/\D/g, "");

      if (!digitsOnly) return "";

      const formatted = digitsOnly.length === 1 ? digitsOnly : `${digitsOnly[0]}.${digitsOnly.slice(1)}`;

      return parseFloat(parseFloat(formatted).toFixed(2));
    },
    PowderAmountWeightCheck(target_value, gun_id) {
      const gun = this.powder_output_measurements.find(gun => gun.gun_id === gun_id);
      if (gun.gun_measurements.some(pair => pair.weight !== "" && pair.weight <= gun.empty_bag_weight)) {
        this.$swal({
          title: "Invalid Powder Output Measurement",
          text: "The Powder Output measurement must be higher than the empty bag weight.",
          icon: "error",
          confirmButtonText: "OK",
        }).then(() => this.removePowderWeightInput(target_value, gun_id));
        return;
      } else if (
        gun.gun_measurements.some(
          (pair, index, array) => index > 0 && pair.weight !== "" && pair.weight <= array[index - 1].weight,
        )
      ) {
        this.$swal({
          title: "Invalid Powder Output Measurement",
          text: "Each Powder Output Measurement must be higher than the previous one.",
          icon: "error",
          confirmButtonText: "OK",
        }).then(() => this.removePowderWeightInput(target_value, gun_id));
        return;
      }
    },
    removePowderWeightInput(target_value, gun_id) {
      const gun = this.powder_output_measurements.find(gun => gun.gun_id === gun_id);
      gun.gun_measurements.map(pair => {
        if (pair.weight == target_value) {
          pair.weight = "";
        }
      });
    },
    async triggerTraining() {
      try {
        await this.storeCurrentBaggingData();
        if (!this.isMichiganRegionUser) {
          await this.getCoatingBooth();
          await this.getPendingCoatingBooths();
          this.$store.commit("addMultiOutputGPModelTrainingInProgress", this.$store.state.user_data.id);
          await axios.post("api/v1/steelcase/launch-model-training/");
          this.is_training_ongoing = true;
        }
        // TEMPORARY HACK
        else if (this.isMichiganRegionUser) {
          // await this.checkIfPendingParameterUpdates();
          await axios.post("api/v1/steelcase/recomputeandsavepowderamountsettingsforcoatingbooth/", {
            coating_booth_id: this.coating_booth.id,
          });
          this.$swal({
            title: "Bagging Completed",
            text: "Bagging data has been successfully saved.",
            icon: "success",
            confirmButtonText: "OK",
          }).then(() => {
            // this.redirectToDiameterUpdate();
            this.$router.push({
              name: "SteelCaseDashboard",
              params: {
                lineId: this.coating_booth.id,
              },
            });
          });
        }
        window.scrollTo({ top: 0, behavior: "smooth" });
      } catch (error) {
        console.error(error);
      }
    },
    async redirectToDiameterUpdate() {
      try {
        const response = await axios.post("/api/v1/steelcase/tempventuridiametermeasurement/", {});
        const diameterMeasurementId = response.data.id;
        this.$router.push({
          name: "DiameterMeasurement",
          params: {
            lineId: this.coating_booth.id,
            diameterMeasurementId: diameterMeasurementId,
          },
          query: { fromBagging: "true" },
        });
      } catch (error) {
        console.error(error);
      }
    },
    getGunPowderOutputMeasurements(current_gun) {
      const gunPowderOutputMeasurements = this.powder_output_measurements.map(gun => {
        const empty = parseFloat(gun.empty_bag_weight);
        let prev = empty;
        const series = gun.gun_measurements.map(measurement => {
          const setting = parseFloat(measurement.setting);
          const raw = parseFloat(measurement.weight);
          const actual = raw - prev;
          prev = raw;
          return { x: setting, y: actual };
        });
        return series;
      });
      return gunPowderOutputMeasurements[current_gun.gun_index - 1];
    },
    getValidationClass(gun_id) {
      const gun = this.gun_data.find(gun => gun.gun_id === gun_id);
      if (!gun) {
        return "";
      } else if (!gun.validation_status) {
        if (this.rebaggingEditableGuns.includes(gun.gun_id.toFixed(0))) {
          return "accepted-row";
        } else if (
          !this.rebaggingEditableGuns.includes(gun.gun_id.toFixed(0)) &&
          this.populateWithLatestBaggingMeasures
        ) {
          return "valid-row";
        } else {
          return "";
        }
      }

      switch (gun.validation_status) {
        case "valid":
          return "valid-row";
        case "reviewing_bagging":
          return "accepted-row";
        case "rejected":
          return "rejected-row";
        default:
          return "";
      }
    },
    onClickPrevPage() {
      if (this.currentPage > this.firstPage) {
        this.currentPage -= 1;
      }
    },
    onClickNextPage() {
      if (this.currentPage < this.lastPage) {
        this.currentPage += 1;
      }
    },
  },
};
</script>
<style scoped>
.table-sm th,
.table-sm td {
  padding: 0.3rem;
}

.container-fluid {
  padding-top: 20px;
}

.valid-row {
  background-color: #d4edda !important;
  /* green */
}

.accepted-row {
  background-color: #fff3cd !important;
  /* yellow */
}

.rejected-row {
  background-color: #f8d7da !important;
  /* red */
}

.color-legend {
  display: flex;
  gap: 15px;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  font-size: 0.9rem;
}

.legend-item {
  display: flex;
  align-items: center;
}

.color-box {
  width: 20px;
  height: 20px;
  display: inline-block;
  border-radius: 3px;
  margin-right: 6px;
  border: 1px solid #ccc;
}

.hover-underline {
  transition: text-decoration 0.2s;
}

tr:hover .hover-underline {
  text-decoration: underline;
}

.previous-bagging-row {
  background: #f5f5f5;
  cursor: pointer;
  border-radius: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  transition:
    box-shadow 0.2s,
    background 0.2s;
}

.previous-bagging-row:hover {
  background: #dedede;
  color: #000000;
  box-shadow: 0 4px 16px rgba(51, 51, 51, 0.18);
}

/* Add spacing between rows */
tbody > .previous-bagging-row:not(:first-child) {
  margin-top: 7px;
}

/* Make the row look like a card */
.previous-bagging-row td {
  border-top: none !important;
}
</style>
